import { useState } from 'react'
import { Card, Select, Button, Space, Empty, Spin } from 'antd'
import { SearchOutlined, DownloadOutlined, UserOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { usersApi } from '@/api/users'
import { PermissionManager } from '@/components/permissions/PermissionManager'
import type { User } from '@/types/user'

export const UserPermissions = () => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch users for search
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', { search: searchTerm }],
    queryFn: () => usersApi.getUsers({ search: searchTerm, limit: 50 }),
    enabled: searchTerm.length > 0,
  })

  // Get selected user details
  const selectedUser = usersData?.data?.find((u: User) => u.id === selectedUserId)

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export permissions for user:', selectedUserId)
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1>User Permissions</h1>
        <p>View and manage permissions by user</p>
      </div>

      {/* User Search & Selection */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Select User
            </label>
            <Space style={{ width: '100%' }} align="center">
              <Select
                showSearch
                placeholder="Search by name or email"
                style={{ width: 400 }}
                loading={usersLoading}
                filterOption={false}
                onSearch={setSearchTerm}
                onChange={setSelectedUserId}
                value={selectedUserId}
                suffixIcon={<SearchOutlined />}
                optionLabelProp="label"
                options={usersData?.data?.map((user: User) => {
                  const hasName = user.first_name && user.last_name
                  const displayName = hasName ? `${user.first_name} ${user.last_name}` : user.email
                  return {
                    value: user.id,
                    label: displayName,
                  }
                })}
                optionRender={(option) => {
                  const user = usersData?.data?.find((u: User) => u.id === option.value)
                  const hasName = user?.first_name && user?.last_name
                  return (
                    <div>
                      <div style={{ fontWeight: 500 }}>{option.data.label}</div>
                      {hasName && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{user?.email}</div>
                      )}
                    </div>
                  )
                }}
                notFoundContent={
                  searchTerm.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      Type to search for users
                    </div>
                  ) : usersLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}><Spin size="small" /></div>
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      No users found
                    </div>
                  )
                }
              />
              {selectedUser && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled
                  title="Export functionality coming soon"
                >
                  Export Permissions
                </Button>
              )}
            </Space>
          </div>

          {selectedUser && (
            <div
              style={{
                padding: '12px 16px',
                background: '#f5f5f5',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <UserOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
              <div>
                <div style={{ fontWeight: 500, fontSize: '16px' }}>
                  {selectedUser.first_name} {selectedUser.last_name}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {selectedUser.email}
                  {selectedUser.active ? (
                    <span style={{ marginLeft: 8, color: '#52c41a' }}>● Active</span>
                  ) : (
                    <span style={{ marginLeft: 8, color: '#ff4d4f' }}>● Inactive</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </Space>
      </Card>

      {/* Permission Management */}
      {selectedUserId ? (
        <PermissionManager userId={selectedUserId} />
      ) : (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a user to view and manage their permissions"
          />
        </Card>
      )}
    </div>
  )
}
